import { AlertTriangle, CheckCircle2, MoreVertical } from "lucide-react";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TimezoneService } from "@/lib/timezone";

interface GoogleConnectionStripProps {
  locationName: string;
  authMode: "OWNER_OAUTH" | "MANAGED_PLATFORM";
  lastSyncAt: string | null | undefined;
  lastSyncError: string | null | undefined;
  reauthRequired: boolean;
  onManage: () => void;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "never";
  return (
    DateTime.fromISO(iso, { zone: "utc" })
      .setZone(TimezoneService.getCurrentSalonTimezone())
      .toRelative() ?? "—"
  );
}

export function GoogleConnectionStrip({
  locationName,
  authMode,
  lastSyncAt,
  lastSyncError,
  reauthRequired,
  onManage,
}: GoogleConnectionStripProps) {
  const hasIssue = reauthRequired || Boolean(lastSyncError);
  const isManaged = authMode === "MANAGED_PLATFORM";

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div
          className={
            hasIssue
              ? "rounded-full bg-destructive/10 p-1.5"
              : "rounded-full bg-emerald-50 dark:bg-emerald-950 p-1.5"
          }
        >
          {hasIssue ? (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {hasIssue ? "Google Business — sync issue" : "Connected to Google Business"}
            </span>
            {isManaged && (
              <span className="rounded-full bg-amber-50 dark:bg-amber-950 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                Managed
              </span>
            )}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {locationName} · Last sync {formatRelative(lastSyncAt)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Connection actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onManage}>Manage connection</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
